/**
 * Cliente de SSE.
 *
 * **Um `EventSource` por tela, e ele fecha quando a tela sai.** Cada stream
 * segura uma conexao do pool da API -- a API abre uma sessao por tique --, e
 * uma aba com a central aberta continua gastando conexao depois de a pessoa
 * ter ido para outra tela. A conexao que sobra e a que faz a API responder
 * "lento" sem nenhum erro no log.
 *
 * **O `EventSource` reconecta sozinho**, e isso e o comportamento certo: quando
 * a casa reinicia, a tela volta a receber sem ninguem mexer. O que o modulo
 * acrescenta e o **callback**, e a garantia de que a conexao antiga foi
 * fechada antes de a nova abrir -- dois streams da mesma tela processam o
 * mesmo evento duas vezes.
 */

/** O que o servidor manda no `data:` de um evento. */
export interface EventoSse {
  id?: string;
  kind?: string;
  criada_em?: string;
}

/**
 * Abre a stream e devolve a funcao que fecha.
 *
 * **Os nomes dos eventos sao um parametro, e nao uma constante.** A central
 * escuta `alerta`; a localizacao escuta cinco (`snapshot`, `location`,
 * `request_answered`, `request_expired`, `requests_expired`). Ficar com um nome
 * fixo aqui obrigaria o mapa a escrever um segundo cliente de SSE -- e dois
 * clientes de SSE no mesmo aplicativo e duas implementacoes de "fecha quando a
 * tela sai", que e a regra que segura conexao do pool da API.
 *
 * **O `withCredentials` e explicito** mesmo estando na mesma origem: o padrao
 * do `EventSource` ja manda o cookie na mesma origem, mas deixar isso no
 * codigo torna a intencao visivel -- e a sessao **e** o cookie, entao um
 * `EventSource` sem credencial e uma tela que nunca recebe nada.
 */
export function abrirStream(
  caminho: string,
  nomes: readonly string[],
  aoEvento: (dados: EventoSse, nome: string) => void,
): () => void {
  const fonte = new EventSource(caminho, { withCredentials: true });

  for (const nome of nomes) {
    // **Um evento nomeado so chega em `addEventListener` com o nome certo.** O
    // `onmessage` so receberia o evento padrao (`message`), e a API **nao**
    // manda `event: message` -- entao, sem esta linha, a tela ficaria em
    // silencio sem erro nenhum.
    fonte.addEventListener(nome, (evento) => {
      const mensagem = evento as MessageEvent<string>;
      try {
        aoEvento(JSON.parse(mensagem.data) as EventoSse, nome);
      } catch {
        // **Um evento malformado nao derruba a tela.** A stream continua, e o
        // proximo evento vem. Um `throw` aqui derrubaria o callback e a central
        // pararia de atualizar -- por causa de um unico evento ruim.
      }
    });
  }

  return () => {
    fonte.close();
  };
}
